/**
 * Date Formatting Utilities
 * Format dates for display and processing
 */
import {
  format,
  parseISO,
  isValid,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  addDays,
  addMonths,
  addYears,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isBefore,
  isAfter,
  isSameDay,
} from 'date-fns';
import { DATE_FORMATS } from '../constants/systemConfig';

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string
 * @returns {string} Formatted date
 */
export const formatDate = (date, formatStr = DATE_FORMATS.DISPLAY) => {
  if (!date) return '';

  const parsedDate = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsedDate)) return '';

  return format(parsedDate, formatStr);
};

/**
 * Format date with time
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date with time
 */
export const formatDateTime = (date) => {
  return formatDate(date, DATE_FORMATS.DISPLAY_WITH_TIME);
};

/**
 * Format date for API (ISO format)
 * @param {Date|string} date - Date to format
 * @returns {string} ISO formatted date
 */
export const formatDateForAPI = (date) => {
  if (!date) return '';

  const parsedDate = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsedDate)) return '';

  return format(parsedDate, DATE_FORMATS.ISO);
};

/**
 * Parse date string to Date object
 * @param {string} dateString - Date string
 * @returns {Date|null} Date object or null
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;

  const parsed = parseISO(dateString);
  return isValid(parsed) ? parsed : null;
};

/**
 * Calculate age from date of birth
 * @param {Date|string} dob - Date of birth
 * @returns {number} Age in years
 */
export const calculateAge = (dob) => {
  if (!dob) return 0;

  const parsedDate = typeof dob === 'string' ? parseISO(dob) : dob;

  if (!isValid(parsedDate)) return 0;

  return differenceInYears(new Date(), parsedDate);
};

/**
 * Calculate days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Days difference
 */
export const daysBetween = (startDate, endDate) => {
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

  if (!isValid(start) || !isValid(end)) return 0;

  return differenceInDays(end, start);
};

/**
 * Check if date is overdue
 * @param {Date|string} dueDate - Due date
 * @returns {boolean} True if overdue
 */
export const isOverdue = (dueDate) => {
  if (!dueDate) return false;

  const parsed = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;

  if (!isValid(parsed)) return false;

  return isBefore(parsed, new Date()) && !isSameDay(parsed, new Date());
};

/**
 * Get relative time (e.g., "2 days ago")
 * @param {Date|string} date - Date
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const parsed = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsed)) return '';

  const now = new Date();
  const days = differenceInDays(now, parsed);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

/**
 * Add time to date
 * @param {Date|string} date - Base date
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit ('days', 'months', 'years')
 * @returns {Date} New date
 */
export const addTime = (date, amount, unit) => {
  const parsed = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsed)) return new Date();

  switch (unit) {
    case 'days':
      return addDays(parsed, amount);
    case 'months':
      return addMonths(parsed, amount);
    case 'years':
      return addYears(parsed, amount);
    default:
      return parsed;
  }
};

/**
 * Get date range for current month
 * @returns {Object} Start and end dates
 */
export const getCurrentMonthRange = () => ({
  start: startOfMonth(new Date()),
  end: endOfMonth(new Date()),
});

/**
 * Get date range for current year
 * @returns {Object} Start and end dates
 */
export const getCurrentYearRange = () => ({
  start: startOfYear(new Date()),
  end: endOfYear(new Date()),
});

/**
 * Check if date is within range
 * @param {Date|string} date - Date to check
 * @param {Date|string} start - Start of range
 * @param {Date|string} end - End of range
 * @returns {boolean} True if within range
 */
export const isWithinRange = (date, start, end) => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  const parsedStart = typeof start === 'string' ? parseISO(start) : start;
  const parsedEnd = typeof end === 'string' ? parseISO(end) : end;

  if (!isValid(parsedDate) || !isValid(parsedStart) || !isValid(parsedEnd)) {
    return false;
  }

  return !isBefore(parsedDate, parsedStart) && !isAfter(parsedDate, parsedEnd);
};

export default {
  formatDate,
  formatDateTime,
  formatDateForAPI,
  parseDate,
  calculateAge,
  daysBetween,
  isOverdue,
  getRelativeTime,
  addTime,
  getCurrentMonthRange,
  getCurrentYearRange,
  isWithinRange,
};
